import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useFirebase } from './FirebaseProvider';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import LoadingSpinner from './LoadingSpinner';
import { useErrorBoundary } from 'react-error-boundary';

export type SimplifiedUser = Pick<User, 'uid' | 'email' | 'emailVerified'>;

export type ShoppingListUserModel = {
  user: SimplifiedUser | null;
  mainListId: string | null;
};

export type UserProfile = { mainListId?: string };

const AuthContext = createContext<ShoppingListUserModel | undefined>(undefined);
AuthContext.displayName = 'CurrentUserContext';

function AuthProvider({ children }: PropsWithChildren) {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<SimplifiedUser | null>(null);
  const [currentMainListId, setCurrentMainListId] = useState<string | null>(null);
  const userId = currentUser?.uid || null;
  const { auth, firestore } = useFirebase();
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      user => {
        if (user) {
          const { uid, email, emailVerified } = user;
          setCurrentUser({
            uid,
            email,
            emailVerified
          });
        } else {
          setCurrentUser(null);
        }
        setCurrentMainListId(null);
        setLoading(false);
      },
      showBoundary
    );

    return () => {
      unsubscribe();
    };
  }, [auth, showBoundary]);

  useEffect(() => {
    if (userId) {
      const unsubscribe = onSnapshot(
        doc(firestore, `users/${userId}`),
        userSnapshot => {
          if (userSnapshot.exists()) {
            const userProfile = userSnapshot.data() as UserProfile;
            setCurrentMainListId(userProfile.mainListId ?? null);
          }
        },
        showBoundary
      );

      return () => {
        unsubscribe();
      };
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentMainListId(null);
    }
  }, [firestore, showBoundary, userId]);

  const value = useMemo(() => ({ user: currentUser, mainListId: currentMainListId }), [currentUser, currentMainListId]);

  return <AuthContext.Provider value={value}>{loading ? <LoadingSpinner /> : children}</AuthContext.Provider>;
}

export function useCurrentUser() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(`useCurrentUser must be used within an AuthProvider`);
  }
  return context;
}

export default AuthProvider;
